import React from 'react';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Typography, Popconfirm, message, Tooltip } from 'antd';
import { intialPaginationState, unexpectedError, showingPageTotalRange, filter } from '../../common_functions';

const NegReq = props => {
  const { section, id } = useParams();
  const [objects, setObjects] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [sorter, setSorter] = useState(null);
  const [loading, setLoading] = useState(false);
  let isDisabled = props.roleType == 'admin' || props.tabs_permissions.negatives?.edit;
  useEffect(() => {
    (async () => {
    //default filter
    let filter = {
      action: {
        _eq: false,
      },
    };
    await getObjectsData(pagination, filter);
  })();
  }, []);
  const getObjectsData = async (pagination, filters = {}, sorter = '') => {
    setLoading(true);
    try {
      const response = await props.getItems('negative_req', {
        fields: ['id,alt_object_title,approved,action,negative.obj_name,print.object_name,negative.c_object,print.c_print'],
        filters: filters,
        sort: !!sorter && sorter.field ? (sorter.order === 'ascend' ? '' : '-') + sorter.field : '-id',
        offset: pagination.pageSize * (pagination.current - 1),
        limit: pagination.pageSize,
        meta: 'filter_count',
      });
      pagination.total = response.meta.filter_count;
      setPagination(pagination);
      setObjects(response.data);
    } catch (error) {
      console.log(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'id',
      key: 'id',
      render: (value, item, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: ' Negative ID',
      dataIndex: 'negative',
      key: 'negative.c_object.id',
      sorter: true,
      ...filter('negative.c_object.id'),
      render: (text, record) => {
        return <>{!!record.negative ? record.negative.c_object : ''}</>;
      },
    },
    {
      title: 'Negative',
      dataIndex: 'negative.obj_name',
      key: 'negative.obj_name',
      sorter: true,
      ...filter('negative.obj_name'),
      render: (text, record) => {
        return <>{!!record.negative ? record.negative.obj_name : ''}</>;
      },
    },
    {
      title: ' Print ID',
      dataIndex: 'print.c_print.id',
      key: 'print.c_print.id',
      sorter: true,
      ...filter('print.c_print.id'),
      render: (text, record) => {
        return <>{!!record.print ? record.print.c_print : ''}</>;
      },
    },
    {
      title: 'Print',
      dataIndex: 'print.object_name',
      key: 'print.object_name',
      sorter: true,
      ...filter('print.object_name'),
      render: (text, record) => {
        return <>{!!record.print ? record.print.object_name : ''}</>;
      },
    },
    {
      title: 'Alt Object Title',
      dataIndex: 'alt_object_title',
      key: 'alt_object_title',
      sorter: true,
      ...filter('alt_object_title'),
      render: (text, record) => {
        return <>{!!record.alt_object_title ? record.alt_object_title : ''}</>;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text, record) => (
        <span>
          <div className="actionbtngroup">
            <Tooltip placement="top" title="Accept">
              <Popconfirm
                placement="left"
                title="Are you sure you want to accept the negative alt object name/title update request?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => acceptRequest(record.id)}
              >
                <button className="edit-btn" disabled={isDisabled ? false : true}>
                  <CheckOutlined />
                </button>
              </Popconfirm>
            </Tooltip>
            <Tooltip placement="top" title="Deny">
              <Popconfirm
                placement="left"
                title="Are you sure you want to deny the negative alt object name/title update request?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => denyRequest(record.id)}
              >
                <button className="edit-btn" disabled={isDisabled ? false : true}>
                  <CloseOutlined />
                </button>
              </Popconfirm>
            </Tooltip>
          </div>
        </span>
      ),
    },
  ];
  const acceptRequest = async reqID => {
    if (!!isDisabled) {
      try {
        let newState = {
          id: reqID,
          approved: true,
          action: true,
        };
        let { data } = await props.saveItem('negative_req', newState);
        if (!!data) {
          setObjects(objects.filter(item => item.id != data.id));
          message.success('Negative update request accepted successfully!');
        }
      } catch (error) {
        message.error('Error occured while accept the negative update request!');
      }
    }
  };
  const denyRequest = async reqID => {
    if (!!isDisabled) {
      try {
        let newState = {
          id: reqID,
          approved: false,
          action: true,
        };
        let { data } = await props.saveItem('negative_req', newState);
        if (!!data) {
          setObjects(objects.filter(item => item.id != data.id));
          message.success('Negative update request denied successfully!');
        }
      } catch (error) {
        message.error('Error occured while denied the negative update request!');
      }
    }
  };
  let handleTableChange = async (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
    setLoading(true);

    let filtersitems = { action: { _eq: false } };

    let filterMapping = {
      'negative.c_object.id': val => ({ negative: { c_object: { id: { _eq: val } } } }),
      'negative.obj_name': val => ({ negative: { obj_name: { _icontains: val } } }),
      'print.c_print.id': val => ({ print: { c_print: { id: { _eq: val } } } }),
      'print.object_name': val => ({ print: { object_name: { _icontains: val } } }),
    };

    let filterConditions = Object.entries(filters).reduce((acc, [key, val]) => {
      if (val?.length) {
        let filterCondition = filterMapping[key] ? filterMapping[key](val[0]) : { [key]: { _icontains: val[0] } };
        acc.push(filterCondition);
      }
      return acc;
    }, []);

    if (filterConditions.length) {
      filtersitems['_and'] = filterConditions;
    }

    await getObjectsData(pagination, filtersitems, sorter);
  };

  return (
    <Row>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Negatives", href: "/negatives/negative-req" }, { title: "Negative Requests" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Negative Requests
              </Typography.Title>
            </Col>
          </Row>
          <hr />
          <div className="users-list">
            {
              <Table
                rowKey="id"
                scroll={{ x: 'max-content' }}
                columns={columns}
                bordered
                dataSource={objects}
                pagination={{ ...pagination, ...showingPageTotalRange }}
                loading={loading}
                onChange={handleTableChange}
              />
            }
          </div>
        </div>
      </Col>
    </Row>
  );
};
export default NegReq;
